<template>
  <div id="main" style="display: flex; flex-direction: column; align-items: center;">
    <div id="report" v-if="report" style="padding: 2rem; display: flex; flex-direction: column; align-items: flex-start; width: 80vw; padding-block: 3rem; background-color: whitesmoke; color: black; border-radius: 0.75rem; display: none;">
      <h3 style="margin: 0; padding: 0;">{{ report.companyName || '[COMPANY NAME]' }}</h3>
      <p style="margin: 0; padding: 0; margin-bottom: 2rem;">{{ report.headquarters || '[HEADQUARTERS ADDRESS]' }}</p>

      <h1 style="align-self: center; font-size: 36px;">Sustainability Report</h1>

      <div style="padding: 1rem; display: flex; flex-direction: column; align-items: flex-start;">
        <h2 style="margin-bottom: 1rem;">Remarks</h2>
        <p style="text-align: left;">The following report has been created following the Voluntary Sustainability Reporting Standard for non-listed micro-, small- and medium-sized undertakings (VSME) published by EFRAG. This report has been created with the help of Suha Oy.</p>

        <div style="height: 0.1rem; margin-block: 3rem; background-color: black; width: 100%;" />

        <div class="html2pdf__page-break" />

        <h2 style="margin-bottom: 1rem; text-align: left; margin-top: 3rem;">B1 - Basis for preparation</h2>
        <p style="text-align: left;">We at {{ report.companyName || '[COMPANY NAME]' }} ({{ report.b124ei || '[BUSINESS ID]' }}) are sharing our VSME report using the basic and comprehensive modules for the period of {{ report.ryfy ? report.ryfy.start : '[FISCAL YEAR START]' }} - {{ report.ryfy ? report.ryfy.end : '[FISCAL YEAR END]' }}.</p>
        <p style="text-align: left; margin-bottom: 1rem;">Here's a quick snapshot of our numbers:</p>
        <p style="text-align: left;">Turnover: {{ report.b124eiv }} €</p>
        <p style="text-align: left;">Balance sheet total: {{ report.b124eiii }} €</p>
        <p style="text-align: left;">NACE code(s): {{ report.b123eii && report.b123eii.join(", ") }}</p>
        <p style="text-align: left;">Operating in: Finland</p>
        <p style="text-align: left; margin-bottom: 1rem;">Team size: {{ report.b124ev }}</p>
        <p style="text-align: left;">Below you can take a look at where our key assets and sites are located:</p>
        <table style="margin-top: 0.5rem;">
          <tr>
            <th>Sites</th>
            <th>Address</th>
            <th>Postal code</th>
            <th>City</th>
            <th>Country</th>
            <th>Coordinates (geolocation)</th>
          </tr>
          <tr v-for="(location, index) in report.b124evii" :key="index">
            <td>{{ `Location ${index + 1}` }}</td>
            <td>{{ capitalize(location.geometry.address.split(",")[0]) }}</td>
            <td>{{ capitalize(String(location.geometry.address.split(",")[1]).trim().split(" ")[0]) }}</td>
            <td>{{ capitalize(String(location.geometry.address.split(",")[1]).trim().split(" ")[1]) }}</td>
            <td>Finland</td>
            <td>{{ `${location.geometry.coordinates.length === 0 ? 'Not found' : `${location.geometry.coordinates[index].lat}, `} ${location.geometry.coordinates.length === 0 ? '' : location.geometry.coordinates[index].lon}` }}</td>
          </tr>
        </table>

        <h2 style="margin-top: 3rem; margin-bottom: 1rem; text-align: left;">B2 - Practices, policies and future initiatives for transitioning towards a more sustainable economy</h2>
        <p style="font-weight: 600; font-style: italic;">NOT AVAILABLE IN FREE VERSION</p>

        <div class="html2pdf__page-break" />

        <h2 style="margin-top: 3rem; margin-bottom: 1rem; text-align: left;">B3 - Energy greenhouse gas emissions</h2>
        <p style="text-align: left;">We've taken a closer look at our total energy consumption and categorized it based on the source. Whether it's green and renewable or still relying on traditional, non-renewable sources, here's how our energy mix looks:</p>
        <table style="margin-block: 1rem;">
          <tr>
            <th></th>
            <th>Renewable energy consumption (MWh)</th>
            <th>Non-renewable energy consumption (MWh)</th>
            <th>Total energy consumption (MWh)</th>
          </tr>
          <tr>
            <th>Electricity</th>
            <td>{{ report.electricityUsage ? (report.electricityUsage.reduce((acc, curr) => acc + Number(curr.total) * (Number(curr.renewable) / 100), 0)) / 1000 : 0 }}</td>
            <td>{{ report.electricityUsage ? (report.electricityUsage.reduce((acc, curr) => acc + Number(curr.total) * (Number(curr.nonRenewable) / 100), 0)) / 1000 : 0 }}</td>
            <td>{{ report.electricityUsage ? (report.electricityUsage.reduce((acc, curr) => acc + Number(curr.total), 0)) / 1000 : 0 }}</td>
          </tr>
          <tr>
            <th>Heating</th>
            <td>{{ report.heating ? (report.heating.reduce((acc, curr) => acc + Number(curr.total) * (Number(curr.renewable) / 100), 0)) / 1000 : 0 }}</td>
            <td>{{ report.heating ? (report.heating.reduce((acc, curr) => acc + Number(curr.total) * (Number(curr.nonRenewable) / 100), 0)) / 1000 : 0 }}</td>
            <td>{{ report.heating ? (report.heating.reduce((acc, curr) => acc + Number(curr.total), 0)) / 1000 : 0 }}</td>
          </tr>
          <tr>
            <th>Fuels</th>
            <td>0</td>
            <td>0</td>
            <td>0</td>
          </tr>
          <tr>
            <th>Total</th>
            <td>{{ report.electricityUsage ? (report.electricityUsage.reduce((acc, curr) => acc + Number(curr.total) * (Number(curr.renewable) / 100), 0)) / 1000 : 0 }}</td>
            <td>{{ report.electricityUsage ? (report.electricityUsage.reduce((acc, curr) => acc + Number(curr.total) * (Number(curr.nonRenewable) / 100), 0)) / 1000 : 0 }}</td>
            <td>{{ report.electricityUsage ? (report.electricityUsage.reduce((acc, curr) => acc + Number(curr.total), 0)) / 1000 : 0 }}</td>
          </tr>
        </table>
        <p v-if="report.electricityUsage && report.electricityUsage.some(i => !i.realTotal)" style="text-align: left; font-style: italic; font-size: 14px;">Note: The report does not include the accurate electricity consumption data for {{ report.electricityUsage.filter(i => !i.realTotal).length }} {{ report.electricityUsage.filter(i => !i.realTotal).length === 1 ? 'property' : 'properties' }} since {{ report.companyName }} is unable or unwilling to provide it. Therefore we have taken liberty to calculate electricity usage based on national averages of commercial properties in Finland, courtesy of Motiva. For the {{ report.electricityUsage.filter(i => !i.realTotal).length === 1 ? 'property' : 'properties' }} without accurate electricity consumption data, we also use the latest (2023) average electricity production mix values, courtesy of Energiateollisuus ry and Tilastokeskus.</p>
        <p v-if="report.heating && report.heating.some(i => !i.realTotal)" style="text-align: left; font-style: italic; font-size: 14px;">Note: The report does not include accurate heating data for {{ report.heating.filter(i => !i.realTotal).length }} {{ report.heating.filter(i => !i.realTotal).length === 1 ? 'property' : 'properties' }} since {{ report.companyName }} is unable or unwilling to provide it. Therefore we have taken liberty to calculate heating based on national averages of commercial properties in Finland, courtesy of Motiva, Tilastokeskus, Energiateollisuus ry and Ympäristöministeriö. For the {{ report.electricityUsage.filter(i => !i.realTotal).length === 1 ? 'property' : 'properties' }} without accurate heating data, we also use average heating energy production mix values, courtesy of Energiateollisuus ry and Motiva, Tilastokeskus and IPCC.</p>
        <br>
        
        <p style="text-align: left; margin-bottom: 1rem; margin-top: 0.5rem;">At {{ report.companyName  || '[COMPANY NAME]' }}, we truly care about our planet and believe in being open about the impact we have. Transparency matters — especially when it comes to emissions. So, in the spirit of keeping it real and doing our part in the collective effort for a more sustainable tomorrow, here's a breakdown of our emissions across all scopes:</p>
        <p style="font-weight: 600; font-style: italic;">NOT AVAILABLE IN FREE VERSION</p>

        <div class="html2pdf__page-break" />

        <h2 style="margin-top: 3rem; margin-bottom: 1rem; text-align: left;">B4 - Pollution of air, water and soil</h2>
        <p style="text-align: left;">Good news — we're not legally required to report on air, water, or soil pollution because… well, we don't pollute them! Our operations don't cause any additional pollution beyond what's already covered in our emissions reporting.</p>
        <p style="text-align: left;">We take pride in keeping our rivers clear, our lakes fresh, and our forests green. Clean business, clean conscience!</p>
        
        <h2 style="margin-top: 3rem; margin-bottom: 1rem; text-align: left;">B5 - Biodiversity</h2>
        <p style="text-align: left;">At {{ report.companyName || '[COMPANY NAME]' }}, we truly care about the world around us. Minimizing our impact on nature and keeping our operations as gentle as possible on the environment is a big part of how we do business.</p>
        <p style="text-align: left;">We've got {{ report.b124evii && (report.b124evii.reduce((acc, curr) => acc + curr.b533, 0)) / 10000 }} hectares located close to biodiversity-sensitive areas, so we're especially mindful of how we operate in those zones.</p>
        <p style="text-align: left; margin-bottom: 1rem;">Here's a little snapshot of our land use:</p>
        <p style="text-align: left;">Total land use: {{ report.b124evii && (report.b124evii.reduce((acc, curr) => acc + Number(curr.sealedArea), 0)) / 10000 }} hectares</p>
        <p style="text-align: left;">Sealed area: {{ report.b124evii && (report.b124evii.reduce((acc, curr) => acc + Number(curr.sealedArea), 0)) / 10000 }} hectares</p>
        <p style="text-align: left;">Nature-oriented areas on-site: {{ report.b124evii && (report.b124evii.reduce((acc, curr) => acc + Number(curr.b534c), 0)) / 10000 }} hectares</p>
        <p style="text-align: left;">Nature-oriented areas off-site: {{ report.b124evii && (report.b124evii.reduce((acc, curr) => acc + Number(curr.b534d), 0)) / 10000 }} hectares</p>

        <div class="html2pdf__page-break" />

        <h2 style="margin-top: 3rem; margin-bottom: 1rem; text-align: left;">B6 - Water</h2>
        <p style="text-align: left;">We totally get it: water is one of the most precious resources on the planet. That's why we keep a close eye on how much we use and do our best to avoid wasting a single drop.</p>
        <p style="text-align: left; margin-block: 1rem;">Our water withdrawal last year: {{ report.waterUsage ? report.waterUsage.reduce((acc, currentValue) => acc + Number(currentValue.total), 0) : 0 }} m3</p>
        <p v-if="report.waterUsage && report.waterUsage.some(i => !i.realTotal)" style="text-align: left; font-style: italic; font-size: 14px;">Note: The report does not include the accurate water withdrawal data for {{ report.waterUsage.filter(i => !i.realTotal).length }} {{ report.waterUsage.filter(i => !i.realTotal).length === 1 ? 'property' : 'properties' }} since {{ report.companyName }} is unable or unwilling to provide it. Therefore we have taken liberty to calculate water usage based on national averages of commercial properties in Finland (0.4m³/m²/v), courtesy of Motiva and HSY.</p>
        <br>
        <p style="text-align: left;">We're also happy to report that none of our sites are located in areas of high water stress. Still, we're staying alert and committed to regularly monitoring local water stress levels to make sure we're doing our part.</p>
        <p style="text-align: left;">Every drop counts — and we're making them all matter.</p>
      
        <div class="html2pdf__page-break" />
        
        <h2 style="margin-top: 3rem; margin-bottom: 1rem; text-align: left;">B7 - Resource use, circular economy and waste management</h2>
        <p style="text-align: left;">We're proud to say we've got waste separation in place as part of our circular economy efforts. It's a small but important step toward building a more sustainable and efficient way of doing business.</p>
        <p style="text-align: left;">While we're still exploring more ways to level up in this area, we know there's plenty of room to grow — and we're excited to integrate even more circular practices into our operations moving forward.</p>
        <p style="text-align: left; margin-block: 1rem;">For now, we're only producing household-type waste, which totaled {{ report.wasteCollection ? (report.wasteCollection.reduce((acc, curr) => acc + curr.realTotal ? Object.values(curr.types).reduce((accumulator, currentValue) => accumulator + currentValue * Number(report.b124evii.find(i => i.geometry.address === curr.address).sealedArea), 0) : Object.values(wasteAverages).reduce((accumulator, currentValue) => accumulator + currentValue.avg * Number(report.b124evii.find(i => i.geometry.address === curr.address).sealedArea), 0), 0)).toFixed(2) : 0 }} m3.</p>
        <p style="text-align: left; margin-top: 0.5rem;">Here's a look at the types of waste we're generating and how much of it we're successfully recycling or reusing:</p>
        <table v-if="report.wasteCollection" style="margin-top: 1rem;">
          <tr>
            <th>Type of waste</th>
            <th>Waste generated (m3)</th>
            <th>Recycled/reused (%)</th>
            <th>Directed to disposal (%)</th>
          </tr>
          <tr v-for="(type, index) of Object.entries(report.wasteCollection[0].types)" :key="index">
            <td>{{ capitalize(type[0].split("Waste")[0]) }}</td>
            <td>{{ (report.wasteCollection.reduce((acc, curr) => acc + curr.realTotal ? Number(curr.types[type[0]]) : wasteAverages[type[0]].avg * Number(report.b124evii.find(i => i.geometry.address === curr.address).sealedArea), 0)).toFixed(2) }}</td>
            <td>{{ (report.wasteCollection.reduce((acc, curr) => acc + wasteAverages[type[0]].recycled, 0) / report.wasteCollection.length).toFixed(2) }}</td>
            <td>{{ (100 - (report.wasteCollection.reduce((acc, curr) => acc + wasteAverages[type[0]].recycled, 0) / report.wasteCollection.length)).toFixed(2) }}</td>
          </tr>
        </table>
        <br>
        <p v-if="report.wasteCollection && report.wasteCollection.some(i => !i.realTotal)" style="text-align: left; font-style: italic; font-size: 14px;">Note: The report does not include the accurate waste generation data for {{ report.wasteCollection.filter(i => !i.realTotal).length }} {{ report.wasteCollection.filter(i => !i.realTotal).length === 1 ? 'property' : 'properties' }} since {{ report.companyName }} is unable or unwilling to provide it. Therefore we have taken liberty to calculate waste generation based on national averages of commercial properties in Finland, courtesy of HSY, Motiva, KIVO, Tilastokeskus. We also use average waste recycling percentages, courtesy of the Finnish Environment Institute (SYKE) and municipal waste authorities.</p>

        <div class="html2pdf__page-break" />
        
        <h2 style="margin-top: 3rem; margin-bottom: 1rem; text-align: left;">B8 - Workforce - General Characteristics</h2>
        <p style="text-align: left;">Here at {{ report.companyName }}, we truly believe our employees are our greatest asset. Without their talent, passion, and dedication, we wouldn't be able to do what we do — plain and simple.</p>
        <p style="text-align: left;">Diversity matters to us, and we're committed to building an inclusive team that reflects the world around us. That's why we're proud to be open and transparent about the amazing people behind our services. They're the real MVPs!</p>
        <p style="text-align: left;">Here's how our team is currently split:</p>
        <table style="margin-top: 1rem;">
          <tr>
            <th>Type of contract</th>
            <th>Number of employees</th>
          </tr>
          <tr>
            <td>Temporary contract</td>
            <td>{{ report.b124ev - report.b839a.permanent }}</td>
          </tr>
          <tr>
            <td>Permanent contract</td>
            <td>{{ report.b839a.permanent }}</td>
          </tr>
        </table>
        <table style="margin-top: 1rem;">
          <tr>
            <th>Gender</th>
            <th>Number of employees</th>
          </tr>
          <tr>
            <td>Male</td>
            <td>{{ report.b839b.male }}</td>
          </tr>
          <tr>
            <td>Female</td>
            <td>{{ report.b839b.female }}</td>
          </tr>
          <tr>
            <td>Other</td>
            <td>{{ report.b839b.other }}</td>
          </tr>
          <tr>
            <td>Not reported</td>
            <td>0</td>
          </tr>
        </table>
        <table style="margin-top: 1rem;">
          <tr>
            <th>Country of employment</th>
            <th>Number of employees</th>
          </tr>
          <tr>
            <td>Finland</td>
            <td>{{ report.b124ev }}</td>
          </tr>
        </table>

        <div class="html2pdf__page-break" />
        
        <h2 style="margin-top: 3rem; margin-bottom: 1rem; text-align: left;">B9 - Workforce - Health and Safety</h2>
        <p style="text-align: left;">At {{ report.companyName || '[COMPANY NAME]' }}, we believe everyone deserves a safe and healthy workplace. That's why we're fully committed to preventing accidents and making sure our employees are well taken care of every day. Their safety is always our top priority.</p>
        <p style="text-align: left;">This past year, we experienced {{ report.b941a }} work-related accident{{ Number(report.b941a) !== 1 ? "s" : "" }}, with an accident rate of {{ report.b941a && report.b124ev && ((Number(report.b941a) / (report.b124ev * 2000)) * 200000).toFixed(2) }}. {{ Number(report.b941a) > 0 ? "While we strive for zero accidents, we're fully aware that we can always improve and are dedicated to making that happen." : "" }}</p>
        <p v-if="report.b941b && Number(report.b941b) > 0" style="text-align: left;">Sadly, we must share that {{ report.b941b }} lives were lost in our operations over the past year. We never take this lightly, and we're working hard to learn from these tragedies and take every step possible to prevent such incidents moving forward.</p>
        <p v-if="!report.b941b || Number(report.b941b) === 0" style="text-align: left;">We're happy to report that no lives were lost in our operations this year.</p>
        
        <h2 style="margin-top: 3rem; margin-bottom: 1rem; text-align: left;">B10 - Workforce - Remuneration, collective bargaining and training</h2>
        <p style="text-align: left;">In Finland, there's no official minimum wage, and at {{ report.companyName || '[COMPANY NAME]' }}, we believe in paying our employees fairly for the great work they do. Because of this, a minimum wage mechanism isn't necessary in our case.</p>
        <p style="text-align: left;">Currently, {{ ((Number(report.b1042c) / Number(report.b124ev)) * 100).toFixed(2) }} % of our employees are covered by a collective bargaining agreement.</p>
        <p style="text-align: left;">As for annual work hours per employee, we've detailed that in the list below:</p>
        <table style="margin-block: 1rem;">
          <tr>
            <th>Gender</th>
            <th>Annual training hours</th>
          </tr>
          <tr>
            <td>Male</td>
            <td>{{ report.b1042d.male }}</td>
          </tr>
          <tr>
            <td>Female</td>
            <td>{{ report.b1042d.female }}</td>
          </tr>
          <tr>
            <td>Other</td>
            <td>{{ report.b1042d.other }}</td>
          </tr>
        </table>
        <p style="text-align: left;">Since we have fewer than 150 employees, we've chosen not to disclose information about the gender pay gap at this time. However, we remain committed to fairness and equality in everything we do.</p>
        
        <div class="html2pdf__page-break" />

        <h2 style="margin-top: 3rem; margin-bottom: 1rem">B11 - Convictions and fines for corruption and bribery </h2>
        <p v-if="!report.convictions" style="text-align: left;">We're happy to share that {{ report.companyName }} has not been convicted of any bribery or corruption charges. We strongly reject any form of illegal political influence and are committed to maintaining the highest standards of integrity in everything we do.</p>
        <p v-if="report.convictions" style="text-align: left;">We acknowledge that {{ report.companyName }} has been convicted of {{ report.convictions }} bribery and/or corruption charge{{ Number(report.convictions) === 1 ? '' : 's' }} with fines totaling {{ report.fines }} €. We deeply regret these incidents, firmly reject any form of illegal political influence, and are committed to strengthening our efforts to uphold the highest standards of integrity in everything we do.</p>

        <div style="height: 0.1rem; margin-block: 5rem; background-color: black; width: 100%;" />

        <div class="html2pdf__page-break" />

        <h2 style="margin-top: 3rem; margin-bottom: 1rem; text-align: left;">C1 - Strategy: Business Model and Sustainability - Related Initiatives</h2>
        <p style="font-weight: 600; font-style: italic;">NOT AVAILABLE IN FREE VERSION</p>

        <h2 style="margin-top: 3rem; margin-bottom: 1rem; text-align: left;">C2 - Description of practices, policies and future initiatives for transitioning towards a more sustainable economy</h2>
        <p style="font-weight: 600; font-style: italic;">NOT AVAILABLE IN FREE VERSION</p>

        <h2 style="margin-top: 3rem; margin-bottom: 1rem; text-align: left;">C3 - GHG reduction targets and climate transition</h2>
        <p style="font-weight: 600; font-style: italic;">NOT AVAILABLE IN FREE VERSION</p>

        <h2 style="margin-top: 3rem; margin-bottom: 1rem; text-align: left;">C4 - Climate risks</h2>
        <p style="font-weight: 600; font-style: italic;">NOT AVAILABLE IN FREE VERSION</p>

        <h2 style="margin-top: 3rem; margin-bottom: 1rem; text-align: left;">C5 - Additional (general) workforce characteristics</h2>
        <p style="text-align: left; margin-top: 0.5rem;">Since we have fewer than 50 employees, we've chosen not to disclose gender diversity information at the company management level.</p>
        
        <div class="html2pdf__page-break" />

        <h2 style="margin-top: 3rem; margin-bottom: 1rem; text-align: left;">C6 - Additional own workforce information - Human rights policies and processes</h2>
        <p style="text-align: left;">At {{ report.companyName }}, we believe that responsible business begins with how we treat our people. Creating fair, safe, and respectful working conditions is at the heart of what we do, and we're committed to protecting the rights and dignity of everyone in our team.</p>
        <p v-if="report.codeOfConduct" style="text-align: left;">We've got a formal Code of Conduct and Human Rights Policy in place for our team. This reflects our ongoing commitment to maintaining the highest labor standards and creating a workplace where everyone feels safe, respected, and empowered.</p>
        <p v-if="!report.codeOfConduct" style="text-align: left;">We don't yet have a formal Code of Conduct or Human Rights Policy for our team and operations, but we've recognized it as an important next step in our sustainability journey. In the past, we didn't feel the need to communicate our values, but we've realized that sharing our stance on these issues is more important than ever in today's world.</p>
        <p v-if="report.codeOfConduct" style="text-align: left; margin-bottom: 1rem;">Our policy specifically addresses:</p>
        <div v-if="report.codeOfConduct">
          <p style="text-align: left;">Child labor: {{ report.codeOfConductBreakdown.childLabor ? "Yes" : "No" }}</p>
          <p style="text-align: left;">Forced labor: {{ report.codeOfConductBreakdown.forcedLabor ? "Yes" : "No" }}</p>
          <p style="text-align: left;">Human trafficking: {{ report.codeOfConductBreakdown.humanTrafficking ? "Yes" : "No" }}</p>
          <p style="text-align: left;">Discrimination: {{ report.codeOfConductBreakdown.discrimination ? "Yes" : "No" }}</p>
          <p style="text-align: left;">Accident prevention & worker safety: {{ report.codeOfConductBreakdown.accidentPreventionAndWorkerSafety ? "Yes" : "No" }}</p>
          <p style="text-align: left;">Other human rights commitments: {{ report.codeOfConductBreakdown.other ? "Yes" : "No" }}</p>
          <p v-if="report.codeOfConductBreakdown.other" style="text-align: left;">{{ report.codeOfConductBreakdown.otherDetails }}</p>
        </div>

        <p style="text-align: left; margin-top: 0.5rem;">By staying informed through global standards and regularly updating our internal practices, we make sure our workforce policies evolve with the world around us.</p>
        <p style="text-align: left; margin-top: 1rem;">At {{ report.companyName }}, we believe that listening builds trust — and we're committed to creating a workplace where every voice matters.</p>
        <div v-if="report.complaintsHandlingMechanism">
          <p style="text-align: left;">We have a dedicated complaints-handling mechanism in place for our workforce. This channel allows team members to safely and confidentially raise any concerns related to working conditions, ethics, or human rights.</p>
          <p style="text-align: left;">Every concern is treated with care, and we are committed to taking timely, transparent action when needed. This ensures that our values are not just words — they are lived experiences, shaped by the people who power our company.</p>
        </div>
        <div v-if="!report.complaintsHandlingMechanism">
          <p style="text-align: left;">While we do not currently have a formal complaints-handling mechanism in place, our size and close-knit culture mean that open, honest communication happens every day. We believe in talking with each other, not over each other, and this ongoing dialogue has so far made a formal system unnecessary.</p>
          <p style="text-align: left;">That said, we remain alert to our team's needs. If the need for a structured mechanism arises in the future, we are fully prepared to put one in place — quickly and thoughtfully — to continue supporting a respectful and responsive work environment.</p>
        </div>

        <div class="html2pdf__page-break" />
        
        <h2 style="margin-top: 3rem; margin-bottom: 1rem; text-align: left;">C7 - Severe negative human rights incidents</h2>
        <div v-if="!Object.values(report.humanRightsIncidents).some(i => i)">
          <p style="text-align: left;">People are the center of everything we do. We're proud to report that we have not encountered any confirmed severe human rights incidents within our workforce.</p>
          <p style="text-align: left;">This reflects our strong culture of respect, our clear workplace policies, and our ongoing commitment to ethical conduct.</p>
        </div>
        <div v-else>
          <p style="text-align: left;">Unfortunately we have reported severe human rights incidents within our workforce. Below is outlined what incidents we have discovered:</p>
          <p style="text-align: left;">Child labor: {{ report.humanRightsIncidents.childLabor ? "Yes" : "No" }}</p>
          <p style="text-align: left;">Forced labor: {{ report.humanRightsIncidents.forcedLabor ? "Yes" : "No" }}</p>
          <p style="text-align: left;">Human trafficking: {{ report.humanRightsIncidents.humanTrafficking ? "Yes" : "No" }}</p>
          <p style="text-align: left;">Discrimination: {{ report.humanRightsIncidents.discrimination ? "Yes" : "No" }}</p>
          <p style="text-align: left;">Other human rights commitments: {{ report.humanRightsIncidents.other ? "Yes" : "No" }}</p>
          <p v-if="report.humanRightsIncidents.other" style="text-align: left;">{{ report.humanRightsIncidents.otherDetails }}</p>
        </div>
        <div v-if="!report.valueChainHumanRightsIncidents">
          <p style="margin-top: 1rem; text-align: left;">We are currently not aware of any confirmed incidents involving human rights violations among our suppliers, partners, affected communities and most importantly, our customers and end-users.</p>
          <p style="text-align: left;">Still, we remain proactive in monitoring these risks and take any early warning signs seriously. We continue to strengthen our due diligence processes to uphold human rights across our entire ecosystem.</p>
        </div>
        <div v-else>
          <p style="text-align: left;">Sadly we have discovered some severe negative human rights incidents within the workforce of our value chain, customers and end-users.</p>
          <p style="text-align: left;">We are committed to making sure that our values align with everyone related to our products/services and we are looking into what could be done to address these incidents.</p>
        </div>
        
        <h2 style="margin-top: 3rem; margin-bottom: 1rem; text-align: left;">C8 - Revenues from certain sectors and exclusion from EU reference benchmarks</h2>
        <p v-if="report.coalTransportationRevenue || report.fuelTransportationRevenue || report.gasTransportationRevenue" style="text-align: left;">During the last fiscal year, our trucks transported fossil fuels which generated a revenue of {{ (report.coalTransporatationRevenue ? Number(report.coalTransporatationRevenue) : 0) + (report.fuelTransportationRevenue ? Number(report.fuelTransportationRevenue) : 0) + (report.gasTransportationRevenue ? Number(report.gasTransportationRevenue) : 0) }} €</p>
        <p v-if="!report.coalTransportationRevenue || !report.fuelTransportationRevenue || !report.gasTransportationRevenue" style="text-align: left;">During the last fiscal year, none of our trucks transported any fossil fuels. We're proud to keep it clean and move toward a greener future!</p>
        <p v-if="!revenuesFromCertainSectors()" style="text-align: left;">We don't {{ report.coalTransporatationRevenue || report.fuelTransportationRevenue || report.gasTransportationRevenue ? 'create enough revenue in any sector' : 'operate in any sectors' }} that would exclude us from the EU's Paris-aligned benchmarks. Staying aligned with climate goals is part of how we do business!</p>
        
        <h2 style="margin-top: 3rem; margin-bottom: 1rem; text-align: left;">C9 - Gender diversity ratio in the governance body</h2>
        <p v-if="Object.values(report.governanceBody).some(i => i)" style="text-align: left; margin-bottom: 2rem;">Our governance body is made up of {{ report.governanceBody.male }} {{ Number(report.governanceBody.male) === 1 ? "man" : "men" }}, {{ report.governanceBody.female }} {{ Number(report.governanceBody.female) === 1 ? "woman" : "women" }}, and {{ report.governanceBody.other }} {{ Number(report.governanceBody.other) === 1 ? "member" : "members" }} of other gender identities. This brings our diversity ratio to {{ (Number(report.governanceBody.female) + Number(report.governanceBody.other)) / Number(report.governanceBody.male) }} — and we're always aiming to keep things balanced and inclusive.</p>
        <p v-if="Object.values(report.governanceBody).every(i => !i)" style="text-align: left; margin-bottom: 2rem;">Because of our company's legal structure, we don't have a formal governance body in place. It's just how we're set up legally — but we still take responsibility and decision-making seriously.</p>
      </div>
    </div>
    <div class="chatbot-toggler" @click="handleChatToggle">
      <span class="material-symbols-outlined">mode_comment</span>
      <span class="material-symbols-outlined">close</span>
    </div>
    <div class="chatbot">
      <header>
        <h2 style="margin: 0; padding: 0;">Vertti Vastuullisuusbotti</h2>
      </header>
      <div class="chatbox">
        <div v-for="(message, index) in visibleMessages" :key="index" :class="`chat ${message.type}`">
          <component
            v-for="(item, ind) in getMessageContent(message)"
            :key="ind"
            :id="item.id || null"
            :is="item.component"
            :class="item.class"
            :type="item.type"
            :disabled="item.component === 'button' && (item.disabled || loading || step > index + 1)"
            :style="{ ...item.style, opacity: item.component === 'button' && (item.disabled || loading || step > index + 1) ? 0.5 : 1 }"
            :placeholder="item.placeholder || null"
            @input="e => setValue(item.model, e.target.value)"
            @click="item.click ? handleEvent(item.click) : () => {}"
            @keypress="item.onKeyPress"
          >
            <div style="display: flex; flex-direction: row; align-items: center;">
              <p style="margin: 0; padding: 0;">{{ item.html }}</p>
              <div v-if="loading && item.component === 'button' && item.html !== '+' && step === index + 1" class="loader"></div>
            </div>
          </component>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
  import db from './firebase'
  import { addDoc, collection } from 'firebase/firestore'
  import html2pdf from 'html2pdf.js'

  const naceCodes = {
    "49000": "H49",
    "49100": "H49.1.0",
    "49200": "H49.2.0",
    "49300": "H49.3",
    "49310": "H49.3.1",
    "49320": "H49.3.2",
    "49390": "H49.3.9",
    "49400": "H49.4",
    "49410": "H49.4.1",
    "49420": "H49.4.2",
    "49500": "H49.5.0",
    "50000": "H50",
    "50100": "H50.1.0",
    "50200": "H50.2.0",
    "50300": "H50.3.0",
    "50400": "H50.4.0",
    "51000": "H51",
    "51100": "H51.1.0",
    "51200": "H51.2",
    "51210": "H51.2.1",
    "51220": "H51.2.2",
    "52000": "H52",
    "52100": "H52.1.0",
    "52200": "H52.2",
    "52210": "H52.2.1",
    "52220": "H52.2.2",
    "52230": "H52.2.3",
    "52240": "H52.2.4",
    "52290": "H52.2.9",
    "53000": "H53",
    "53100": "H53.1.0",
    "53200": "H53.2.0",
    "96090": "S96.0.9",
  }

  export default {
    data() {
      return {
        loading: false,
        step: 0,
        report: {
          email: "",
          companyId: "",
          created: new Date(),
          status: "pending",
          step: 0,
          headquarters: null,
          b1042c: 0,
          b1042d: {
            female: 0,
            male: 0,
          },
          b123eii: [],
          b124ai: false,
          b124aii: true,
          b124cc: true,
          b124ci: false,
          b124ei: "",
          b124eiii: 0,
          b124eiv: 0,
          b124ev: 0,
          b124evi: ['finland'],
          b124evii: [
            {
              geometry: {
                address: "",
                coordinates: [],
              },
              buildingsArea: 0,
              sealedArea: 0,
              b534c: 0,
              b534d: 0,
            },
          ],
          b1b: [],
          b533: 0,
          b534a: 0,
          b534b: 0,
          b635: 0,
          b738a: 0,
          b839a: {
            permanent: 0,
            temporary: 0,
          },
          b839b: {
            female: 0,
            male: 0,
            other: 0,
          },
          b839c: [{ amount: 0, country: 'finland' }],
          b941a: 0,
          b941b: 0,
          convictions: null,
          fines: null,
          coalTransportationRevenue: null,
          fuelTransportationRevenue: null,
          gasTransportationRevenue: null,
          ryfy: {
            end: "",
            start: "",
          },
          governanceBody: {
            male: 0,
            female: 0,
            other: 0,
          },
          codeOfConduct: null,
          codeOfConductBreakdown: {
            childLabor: false,
            forcedLabor: false,
            humanTrafficking: false,
            discrimination: false,
            accidentPreventionAndWorkerSafety: false,
            other: false,
            otherDetails: null,
          },
          complaintsHandlingMechanism: null,
          humanRightsIncidents: {
            childLabor: false,
            forcedLabor: false,
            humanTrafficking: false,
            discrimination: false,
            other: false,
            otherDetails: null,
          },
          humanRightsIncidentsDesc: null,
          valueChainHumanRightsIncidents: null,
          valueChainHumanRightsIncidentsDesc: null,
        },
        messages: [],
        heatingEnergySourcesList: {
          "01": {
            labelFin: "Kauko- tai aluelämpö",
            labelEng: "District or local heating",
            value: "district_heating",
            avgConsumption: 140,
            avgRenewable: 45,
            avgNonRenewable: 55,
          },
          "02": {
            labelFin: "Kevyt polttoöljy",
            labelEng: "Light fuel oil",
            value: "light_fuel_oil",
            avgConsumption: 200,
            avgRenewable: 0,
            avgNonRenewable: 100,
          },
          "03": {
            labelFin: "Raskas polttoöljy",
            labelEng: "Heavy fuel oil",
            value: "heavy_fuel_oil",
            avgConsumption: 225,
            avgRenewable: 0,
            avgNonRenewable: 100,
          },
          "04": {
            labelFin: "Sähkö",
            labelEng: "Electricity",
            value: "electricity",
            avgConsumption: 175,
            avgRenewable: 52,
            avgNonRenewable: 48,
          },
          "05": {
            labelFin: "Kaasu",
            labelEng: "Gas",
            value: "gas",
            avgConsumption: 160,
            avgRenewable: 7.5,
            avgNonRenewable: 92.5,
          },
          "06": {
            labelFin: "Kivihiili",
            labelEng: "Coal",
            value: "coal",
            avgConsumption: 225,
            avgRenewable: 0,
            avgNonRenewable: 100,
          },
          "07": {
            labelFin: "Puu",
            labelEng: "Wood",
            value: "wood",
            avgConsumption: 125,
            avgRenewable: 100,
            avgNonRenewable: 0,
          },
          "08": {
            labelFin: "Turve",
            labelEng: "Peat",
            value: "peat",
            avgConsumption: 200,
            avgRenewable: 0,
            avgNonRenewable: 100,
          },
          "09": {
            labelFin: "Maalämpö tms.",
            labelEng: "Geothermal heat or similar",
            value: "geothermal_heat_or_similar",
            avgConsumption: 100,
            avgRenewable: 97.5,
            avgNonRenewable: 2.5,
          },
          "10": {
            labelFin: "Aurinkoenergia",
            labelEng: "Solar energy",
            value: "solar_energy",
            avgConsumption: 37.5,
            avgRenewable: 100,
            avgNonRenewable: 0,
          },
          "1101": {
            labelFin: "Maalämpöpumppu",
            labelEng: "Ground-source heat pump",
            value: "gshp",
            avgConsumption: 100,
            avgRenewable: 97.5,
            avgNonRenewable: 2.5,
          },
          "1102": {
            labelFin: "Ilmalämpöpumppu",
            labelEng: "Air-source heat pump",
            value: "ashp",
            avgConsumption: 125,
            avgRenewable: 87.5,
            avgNonRenewable: 12.5,
          },
          "1103": {
            labelFin: "Poistoilmalämpöpumppu",
            labelEng: "Exhaust air heat pump",
            value: "eahp",
            avgConsumption: 110,
            avgRenewable: 87.5,
            avgNonRenewable: 12.5,
          },
          "1104": {
            labelFin: "Ilma-vesilämpöpumppu",
            labelEng: "Air-to-water heat pump",
            value: "air-to-water_heat_pump",
            avgConsumption: 120,
            avgRenewable: 87.5,
            avgNonRenewable: 12.5,
          },
        },
        wasteAverages: {
          "mixedWaste": {
            avg: 0.018,
            recycled: 5,
          },
          "bioWaste": {
            avg: 0.001,
            recycled: 70,
          },
          "plastic": {
            avg: 0.016,
            recycled: 40,
          },
          "cardboard": {
            avg: 0.017,
            recycled: 90,
          },
          "paper": {
            avg: 0.0025,
            recycled: 85,
          },
          "glass": {
            avg: 0.0004,
            recycled: 92.5,
          },
          "metal": {
            avg: 0.0008,
            recycled: 90,
          },
        }
      }
    },
    computed: {
      visibleMessages() {
        return this.messages.slice(0, this.step);
      }
    },
    watch: {
      report: {
        handler: val => {
          if (document.getElementById('codeOfConduct')) {
            val.codeOfConduct = document.getElementById('codeOfConduct').checked
              ? true
              : false
          }
          if (document.getElementById('complaintsHandlingMechanism')) {
            val.complaintsHandlingMechanism = document.getElementById('complaintsHandlingMechanism').checked
              ? true
              : false
          }
          if (document.getElementById('valueChainHumanRightsIncidents')) {
            val.valueChainHumanRightsIncidents = document.getElementById('valueChainHumanRightsIncidents').checked
              ? true
              : false
          }
          val.b124ev = Number(val.b839b.male) + Number(val.b839b.female) + Number(val.b839b.other)
        },
        deep: true,
      },
    },
    async mounted() {
      this.messages.push(...[
        {
          "type": "incoming",
          "content": [
            {
              component: "p",
              html: "Moikka 👋",
              model: null,
            },
          ],
          "waitForAction": false,
          "readDuration": 1000
        },
        {
          "type": "incoming",
          "content": [
            {
              component: "p",
              html: "Tervetuloa Suhan VSME-raportoinnin työkaluun!",
              model: null,
            },
          ],
          "waitForAction": false,
          "readDuration": 1500
        },

        {
          "type": "incoming",
          "content": [
            {
              component: "p",
              html: 'Ensiksi tarvitsemme yrityksenne y-tunnuksen',
              model: null,
            },
          ],
          "waitForAction": false,
          "readDuration": 1500,
        },
        {
          "type": "outgoing",
          "contentBuilder": () => [
            {
              component: "input",
              class: "input",
              html: null,
              model: 'report.b124ei',
              placeholder: "Y-tunnus",
            },
            {
              component: "br",
              html: "",
              model: null,
            },
            {
              component: "br",
              html: "",
              model: null,
            },
            {
              component: "button",
              html: "Jatka",
              model: null,
              disabled: this.report.b124ei === "",
              class: "action-btn documents-ready light",
              click: "getCompanyData",
            },
          ],
          "waitForAction": true,
          "readDuration": 3000,
        },

        {
          "type": "incoming",
          "content": [
            {
              component: "p",
              html: "Kiitos, y-tunnuksen avulla saamme haettua tietoja joilla luomme osan raportista automaattisesti. Meidän tarvitsee kuitenkin kysyä vielä tarkentavia kysymyksiä, jotta saamme raportin valmiiksi.",
              model: null,
            }
          ],
          "waitForAction": false,
          "readDuration": 3000
        },

        {
          "type": "incoming",
          "content": [
            {
              "component": "p",
              "html": "Yrityksen omistamat tai vuokraamat kiinteistöt?",
              "model": null
            }
          ],
          "waitForAction": false,
          "readDuration": 1500,
        },
        {
          "type": "outgoing",
          "contentBuilder": () => [
            ...this.report.b124evii.flatMap((item, index) => [
              {
                component: "input",
                class: "input",
                placeholder: "Testitie 1, 00100 Helsinki",
                html: null,
                model: `report.b124evii.${index}.geometry.address`,
              },
              {
                component: "br",
                html: "",
                model: null,
              },
            ]),
            {
              component: "button",
              html: "+",
              model: null,
              class: "action-btn documents-ready light round",
              click: "addProperty",
            },
            {
              component: "br",
              html: "",
              model: null,
            },
            {
              component: "br",
              html: "",
              model: null,
            },
            {
              component: "button",
              html: "Jatka",
              model: null,
              disabled: this.report.b124evii[0].geometry.address === "",
              class: "action-btn documents-ready light",
              click: "addProperties",
            },
          ],
          "waitForAction": true,
          "readDuration": 3000,
        },

        {
          "type": "incoming",
          "content": [
            {
              component: "p",
              html: "Yrityksen käytössä oleva pinta-ala ja yrityksen luomat luontopainotteiset alueet?",
              model: null,
            },
          ],
          "waitForAction": false,
          "readDuration": 1500
        },
        {
          "type": "incoming",
          "content": [
            {
              component: "p",
              html: "Luontopainotteisella alueella tarkoitetaan aluetta, jonka tarkoitus on tukea luonnon monimuotoisuutta.",
              model: null,
            },
          ],
          "waitForAction": false,
          "readDuration": 3000
        },
        {
          "type": "outgoing",
          "contentBuilder": () => [
            ...this.report.b124evii.flatMap((item, index) => [
              {
                component: "h5",
                html: item.geometry.address,
                model: null,
              },
              {
                component: "p",
                html: `Yhteensä: ${item.buildingsArea} m2`,
                model: null,
              },
              {
                component: "input",
                class: "input",
                placeholder: "Käytetty ala (m2)",
                html: null,
                model: `report.b124evii.${index}.sealedArea`,
                onKeyPress: e => {
                  if (!/\d/.test(e.key) && e.key !== '.') return e.preventDefault()
                },
              },
              {
                "component": "br",
                "html": "",
                "model": "",
              },
              {
                component: "input",
                class: "input",
                placeholder: "Luontopainotteiset alueet tontilla (m2)",
                html: null,
                model: `report.b124evii.${index}.b534c`,
                onKeyPress: e => {
                  if (!/\d/.test(e.key) && e.key !== '.') return e.preventDefault()
                },
              },
              {
                "component": "br",
                "html": "",
                "model": "",
              },
              {
                component: "input",
                class: "input",
                placeholder: "Luontopainotteiset alueet tontin lähellä (m2)",
                html: null,
                model: `report.b124evii.${index}.b534d`,
                onKeyPress: e => {
                  if (!/\d/.test(e.key) && e.key !== '.') return e.preventDefault()
                },
              },
              {
                "component": "br",
                "html": "",
                "model": "",
              },
              {
                "component": "br",
                "html": "",
                "model": "",
              },
            ]),
            {
              component: "button",
              html: "Jatka",
              model: null,
              class: "action-btn documents-ready light",
              click: "saveProperties",
            },
          ],
          "waitForAction": true,
          "readDuration": 3000
        },

        /* {
          "type": "incoming",
          "content": [
            {
              "component": "p",
              "html": "Kiinteistöjen sähkönkulutus viime tilikaudella? Jätä kohdat tyhjäksi mikäli et saa sähkölaskua.",
              "model": null
            }
          ],
          "waitForAction": false,
          "readDuration": 1500,
        },
        {
          "type": "outgoing",
          "contentBuilder": () => [
            ...this.report.b124evii.flatMap((item, index) => [
              {
                component: "h5",
                html: item.geometry.address,
                model: null,
              },
              {
                component: "input",
                class: "input",
                placeholder: "Sähkönkulutus (kWh)",
                html: null,
                model: `report.electricityUsage.${index}.total`,
              },
              {
                component: "input",
                class: "input",
                placeholder: "Uusiutuvan energian osuus? (%)",
                html: null,
                model: `report.electricityUsage.${index}.renewable`,
              },
              {
                component: "input",
                class: "input",
                placeholder: "Uusiutumattoman energian osuus? (%)",
                html: null,
                model: `report.electricityUsage.${index}.nonRenewable`,
              },
              {
                "component": "br",
                "html": "",
                "model": "",
              },
              {
                "component": "br",
                "html": "",
                "model": "",
              },
            ]),
            {
              component: "button",
              html: "Jatka",
              model: null,
              class: "action-btn documents-ready light",
              click: "saveElectricityUsage",
            },
          ],
          "waitForAction": true,
          readDuration: 3000,
        }, */

        /* {
          "type": "incoming",
          "content": [
            {
              "component": "p",
              "html": "Kiinteistöjen lämmitys viime tilikaudella? Jätä tyjäksi mikäli et saa lämmityslaskua.",
              "model": null
            }
          ],
          "waitForAction": false,
          "readDuration": 1500,
        },
        {
          "type": "outgoing",
          "contentBuilder": () => [
            ...this.report.b124evii.flatMap((item, index) => [
              {
                component: "h5",
                html: item.geometry.address,
                model: null,
              },
              {
                component: "p",
                html: `Lämmitysmuodot: ${item.heatingEnergySources.filter(i => i.heatingSource).map(i => this.heatingEnergySourcesList[i.heatingSource].labelFin.toLowerCase())}`,
                model: null,
              },
              {
                component: "input",
                class: "input",
                placeholder: "Lämmitysenergia (kWh)",
                html: null,
                model: `report.heating.${index}.total`,
              },
              {
                component: "input",
                class: "input",
                placeholder: "Uusiutuvan energian osuus? (%)",
                html: null,
                model: `report.heating.${index}.renewable`,
              },
              {
                component: "input",
                class: "input",
                placeholder: "Uusiutumattoman energian osuus? (%)",
                html: null,
                model: `report.heating.${index}.nonRenewable`,
              },
              {
                "component": "br",
                "html": "",
                "model": "",
              },
              {
                "component": "br",
                "html": "",
                "model": "",
              },
            ]),
            {
              component: "button",
              html: "Jatka",
              model: null,
              class: "action-btn documents-ready light",
              click: "saveHeating",
            },
          ],
          "waitForAction": true,
          readDuration: 3000,
        }, */

        /* {
          "type": "incoming",
          "content": [
            {
              "component": "p",
              "html": "Kiinteistöjen vedenkulutus viime tilikaudella? Jätä tyhjäksi mikäli et saa vesilaskua.",
              "model": null
            },
          ],
          "waitForAction": false,
          "readDuration": 1500,
        },
        {
          "type": "outgoing",
          "contentBuilder": () => [
            ...this.report.b124evii.flatMap((item, index) => [
              {
                component: "h5",
                html: item.geometry.address,
                model: null,
              },
              {
                component: "input",
                class: "input",
                placeholder: "Vedenkulutus (m3)",
                html: null,
                model: `report.waterUsage.${index}.total`,
              },
              {
                "component": "br",
                "html": "",
                "model": "",
              },
              {
                "component": "br",
                "html": "",
                "model": "",
              },
            ]),
            {
              component: "button",
              html: "Jatka",
              model: null,
              class: "action-btn documents-ready light",
              click: "saveWaterUsage",
            },
          ],
          "waitForAction": true,
          readDuration: 3000,
        }, */

        /* {
          "type": "incoming",
          "content": [
            {
              "component": "p",
              "html": "Kiinteistöjen jätteenkeräys viime tilikaudella? Jätä tyhjäksi mikäli et saa jätteenkeräyslaskua.",
              "model": null
            }
          ],
          "waitForAction": false,
          "readDuration": 1500,
        },
        {
          "type": "outgoing",
          "contentBuilder": () => [
            ...this.report.b124evii.flatMap((item, index) => [
              {
                component: "h5",
                html: item.geometry.address,
                model: null,
              },
              {
                component: "input",
                class: "input",
                placeholder: "Sekajäte (m3)",
                html: null,
                model: `report.wasteCollection.${index}.types.mixedWaste`,
              },
              {
                component: "input",
                class: "input",
                placeholder: "Biojäte (m3)",
                html: null,
                model: `report.wasteCollection.${index}.types.bioWaste`,
              },
              {
                component: "input",
                class: "input",
                placeholder: "Muovi (m3)",
                html: null,
                model: `report.wasteCollection.${index}.types.plastic`,
              },
              {
                component: "input",
                class: "input",
                placeholder: "Kartonki (m3)",
                html: null,
                model: `report.wasteCollection.${index}.types.cardboard`,
              },
              {
                component: "input",
                class: "input",
                placeholder: "Paperi (m3)",
                html: null,
                model: `report.wasteCollection.${index}.types.paper`,
              },
              {
                component: "input",
                class: "input",
                placeholder: "Lasi (m3)",
                html: null,
                model: `report.wasteCollection.${index}.types.glass`,
              },
              {
                component: "input",
                class: "input",
                placeholder: "Metalli (m3)",
                html: null,
                model: `report.wasteCollection.${index}.types.metal`,
              },
              {
                "component": "br",
                "html": "",
                "model": "",
              },
              {
                "component": "br",
                "html": "",
                "model": "",
              },
            ]),
            {
              component: "button",
              html: "Jatka",
              model: null,
              class: "action-btn documents-ready light",
              click: "saveWasteCollection",
            },
          ],
          "waitForAction": true,
          readDuration: 3000,
        }, */

        {
          "type": "incoming",
          "content": [
            {
              component: "p",
              html: "Työntekijöiden sukupuolijakauma ja keskimääräiset harjoittelutunnit vuodessa?",
              model: null,
            }
          ],
          "waitForAction": false,
          "readDuration": 1500
        },
        {
          "type": "incoming",
          "content": [
            {
              component: "p",
              html: "Harjoittelutunneilla tarkoitetaan esimerkiksi koulutuspäiviä tai muuta ajankäyttöä, jonka tarkoitus on kehittää työntekijän osaamista.",
              model: null,
            },
          ],
          "waitForAction": false,
          "readDuration": 3000
        },
        {
          "type": "outgoing",
          "contentBuilder": () => [
            {
              component: "h5",
              html: "Mies",
              model: null,
            },
            {
              component: "input",
              class: "input",
              placeholder: "Kokonaismäärä",
              html: null,
              model: "report.b839b.male",
              onKeyPress: e => {
                if (!/\d/.test(e.key) && e.key !== '.') return e.preventDefault()
              },
            },
            {
              "component": "br",
              "html": "",
              "model": "",
            },
            {
              component: "input",
              class: "input",
              placeholder: "Määrä hallituksessa",
              html: null,
              model: "report.governanceBody.male",
              onKeyPress: e => {
                if (!/\d/.test(e.key) && e.key !== '.') return e.preventDefault()
              },
            },
            {
              "component": "br",
              "html": "",
              "model": "",
            },
            {
              component: "input",
              class: "input",
              placeholder: "Keskimääräiset harjoittelutunnit",
              html: null,
              model: "report.b1042d.male",
              onKeyPress: e => {
                if (!/\d/.test(e.key) && e.key !== '.') return e.preventDefault()
              },
            },
            {
              component: "br",
              html: "",
              model: null,
            },
            {
              component: "br",
              html: "",
              model: null,
            },
            {
              component: "h5",
              html: "Nainen",
              model: null,
            },
            {
              component: "input",
              class: "input",
              placeholder: "Kokonaismäärä",
              html: null,
              model: "report.b839b.female",
              onKeyPress: e => {
                if (!/\d/.test(e.key) && e.key !== '.') return e.preventDefault()
              },
            },
            {
              "component": "br",
              "html": "",
              "model": "",
            },
            {
              component: "input",
              class: "input",
              placeholder: "Määrä hallituksessa",
              html: null,
              model: "report.governanceBody.female",
              onKeyPress: e => {
                if (!/\d/.test(e.key) && e.key !== '.') return e.preventDefault()
              },
            },
            {
              "component": "br",
              "html": "",
              "model": "",
            },
            {
              component: "input",
              class: "input",
              placeholder: "Keskimääräiset harjoittelutunnit",
              html: null,
              model: "report.b1042d.female",
              onKeyPress: e => {
                if (!/\d/.test(e.key) && e.key !== '.') return e.preventDefault()
              },
            },
            {
              component: "br",
              html: "",
              model: null,
            },
            {
              component: "br",
              html: "",
              model: null,
            },
            {
              component: "h5",
              html: "Muunsukupuolinen",
              model: null,
            },
            {
              component: "input",
              class: "input",
              placeholder: "Kokonaismäärä",
              html: null,
              model: "report.b839b.other",
              onKeyPress: e => {
                if (!/\d/.test(e.key) && e.key !== '.') return e.preventDefault()
              },
            },
            {
              "component": "br",
              "html": "",
              "model": "",
            },
            {
              component: "input",
              class: "input",
              placeholder: "Määrä hallituksessa",
              html: null,
              model: "report.governanceBody.other",
              onKeyPress: e => {
                if (!/\d/.test(e.key) && e.key !== '.') return e.preventDefault()
              },
            },
            {
              "component": "br",
              "html": "",
              "model": "",
            },
            {
              component: "input",
              class: "input",
              placeholder: "Keskimääräiset harjoittelutunnit",
              html: null,
              model: "report.b1042d.other",
              onKeyPress: e => {
                if (!/\d/.test(e.key) && e.key !== '.') return e.preventDefault()
              },
            },
            {
              component: "br",
              html: "",
              model: null,
            },
            {
              "component": "br",
              "html": "",
              "model": "",
            },
            {
              component: "button",
              html: "Jatka",
              model: null,
              disabled: [...Object.values(this.report.b1042d), ...Object.values(this.report.governanceBody), ...Object.values(this.report.b839b)].every(val => val < 1),
              class: "action-btn documents-ready light",
              click: "test",
            },
          ],
          "waitForAction": true,
          "readDuration": 3000
        },
        {
          "type": "incoming",
          "content": [
            {
              component: "p",
              html: "Vakituisten työsopimusten määrä?",
              model: null,
            },
          ],
          "waitForAction": false,
          "readDuration": 1500
        },
        {
          "type": "outgoing",
          "content": [
            {
              component: "input",
              class: "input",
              placeholder: "Vakituiset työsopimukset",
              html: null,
              model: "report.b839a.permanent",
              onKeyPress: e => {
                if (!/\d/.test(e.key) && e.key !== '.') return e.preventDefault()
              },
            },
            {
              component: "br",
              html: "",
              model: null,
            },
            {
              "component": "br",
              "html": "",
              "model": "",
            },
            {
              component: "button",
              html: "Jatka",
              model: null,
              class: "action-btn documents-ready light",
              click: "test",
            },
          ],
          "waitForAction": true,
          "readDuration": 3000
        },
        {
          "type": "incoming",
          "content": [
            {
              component: "p",
              html: "Työehtosopimuksen piiriin kuuluvien määrä?",
              model: null,
            },
          ],
          "waitForAction": false,
          "readDuration": 1500
        },
        {
          "type": "outgoing",
          "content": [
            {
              component: "input",
              class: "input",
              placeholder: "Työehtosopimuksen alla olevat",
              html: null,
              model: "report.b1042c",
              onKeyPress: e => {
                if (!/\d/.test(e.key) && e.key !== '.') return e.preventDefault()
              },
            },
            {
              component: "br",
              html: "",
              model: null,
            },
            {
              "component": "br",
              "html": "",
              "model": "",
            },
            {
              component: "button",
              html: "Jatka",
              model: null,
              class: "action-btn documents-ready light",
              click: "test",
            },
          ],
          "waitForAction": true,
          "readDuration": 3000
        },
        {
          "type": "incoming",
          "content": [
            {
              component: "p",
              html: "Työpaikkaonnettomuuksien ja -kuolemien määrä viime tilikaudella?",
              model: null,
            },
          ],
          "waitForAction": false,
          "readDuration": 1500
        },
        {
          "type": "outgoing",
          "content": [
            {
              component: "input",
              class: "input",
              placeholder: "Onnettomuudet",
              html: null,
              model: "report.b941a",
              onKeyPress: e => {
                if (!/\d/.test(e.key) && e.key !== '.') return e.preventDefault()
              },
            },
            {
              component: "br",
              html: "",
              model: null,
            },
            {
              component: "input",
              class: "input",
              placeholder: "Kuolemat",
              html: null,
              model: "report.b941b",
              onKeyPress: e => {
                if (!/\d/.test(e.key) && e.key !== '.') return e.preventDefault()
              },
            },
            {
              component: "br",
              html: "",
              model: null,
            },
            {
              "component": "br",
              "html": "",
              "model": "",
            },
            {
              component: "button",
              html: "Jatka",
              model: null,
              class: "action-btn documents-ready light",
              click: "test",
            },
          ],
          "waitForAction": true,
          "readDuration": 3000
        },

        {
          "type": "incoming",
          "content": [
            {
              component: "p",
              html: "Tuomiot ja sakot korruptiosta ja/tai lahjonnasta viime tilikaudella? Jätä tyhjäksi mikäli nämä eivät koske yritystäsi.",
              model: null,
            },
          ],
          "waitForAction": false,
          "readDuration": 1500
        },
        {
          "type": "outgoing",
          "content": [
            {
              component: "input",
              class: "input",
              placeholder: "Tuomiot",
              html: null,
              model: "report.convictions",
              onKeyPress: e => {
                if (!/\d/.test(e.key) && e.key !== '.') return e.preventDefault()
              },
            },
            {
              component: "br",
              html: "",
              model: null,
            },
            {
              component: "input",
              class: "input",
              placeholder: "Sakkojen yhteenlaskettu määrä (€)",
              html: null,
              model: "report.fines",
              onKeyPress: e => {
                if (!/\d/.test(e.key) && e.key !== '.') return e.preventDefault()
              },
            },
            {
              component: "br",
              html: "",
              model: null,
            },
            {
              "component": "br",
              "html": "",
              "model": "",
            },
            {
              component: "button",
              html: "Jatka",
              model: null,
              class: "action-btn documents-ready light",
              click: "test",
            },
          ],
          "waitForAction": true,
          "readDuration": 3000
        },

        {
          "type": "incoming",
          "content": [
            {
              component: "p",
              html: "Viime tilikauden liikevaihto fossiilisten polttoaineiden kuljettamisesta? Jätä tyhjäksi mikäli nämä eivät koske yritystäsi.",
              model: null,
            },
          ],
          "waitForAction": false,
          "readDuration": 1500
        },
        {
          "type": "outgoing",
          "contentBuilder": () => [
            {
              component: "input",
              class: "input",
              placeholder: "Liikevaihto kivihiilen kuljettamisesta (€)",
              html: null,
              model: "report.coalTransportationRevenue",
              onKeyPress: e => {
                if (!/\d/.test(e.key) && e.key !== '.') return e.preventDefault()
              },
            },
            {
              component: "br",
              html: "",
              model: null,
            },
            {
              component: "input",
              class: "input",
              placeholder: "Liikevaihto nestemäisten polttoaineiden kuljettamisesta (€)",
              html: null,
              model: "report.fuelTransportationRevenue",
              onKeyPress: e => {
                if (!/\d/.test(e.key) && e.key !== '.') return e.preventDefault()
              },
            },
            {
              component: "br",
              html: "",
              model: null,
            },
            {
              component: "input",
              class: "input",
              placeholder: "Liikevaihto kaasumaisten polttoaineiden kuljettamisesta (€)",
              html: null,
              model: "report.gasTransportationRevenue",
              onKeyPress: e => {
                if (!/\d/.test(e.key) && e.key !== '.') return e.preventDefault()
              },
            },
            {
              component: "br",
              html: "",
              model: null,
            },
            {
              "component": "br",
              "html": "",
              "model": null,
            },
            {
              component: "button",
              html: "Jatka",
              model: null,
              class: "action-btn documents-ready light",
              click: "test",
            },
          ],
          "waitForAction": true,
          "readDuration": 3000
        },

        {
          "type": "incoming",
          "content": [
            {
              component: "p",
              html: "Onko yrityksessänne valitusten käsittelyjärjestelmää?",
              model: null,
            },
          ],
          "waitForAction": false,
          "readDuration": 1500
        },
        {
          "type": "outgoing",
          "content": [
            {
              component: "label",
              html: "Valitusten käsittelyjärjestelmä olemassa",
              model: null,
            },
            {
              component: "br",
              html: "",
              model: null,
            },
            {
              id: "complaintsHandlingMechanism",
              component: "input",
              type: "checkbox",
              html: null,
              model: "report.complaintsHandlingMechanism",
            },
            {
              component: "br",
              html: "",
              model: null,
            },
            {
              "component": "br",
              "html": "",
              "model": null,
            },
            {
              component: "button",
              html: "Jatka",
              model: null,
              class: "action-btn documents-ready light",
              click: "test",
            },
          ],
          "waitForAction": true,
          "readDuration": 3000
        },

        {
          "type": "incoming",
          "content": [
            {
              component: "p",
              html: "Onko yrityksessänne eettisyyttä ja ihmisoikeuksia koskevaa toimintaohjeistusta?",
              model: null,
            },
          ],
          "waitForAction": false,
          "readDuration": 1500
        },
        {
          "type": "outgoing",
          "contentBuilder": () => [
            {
              component: "label",
              html: "Eettisyyttä ja ihmisoikeuksia koskeva toimintaohjeistus olemassa",
              model: null,
            },
            {
              component: "br",
              html: "",
              model: null,
            },
            {
              id: "report.codeOfConduct",
              component: "input",
              type: "checkbox",
              html: null,
              model: "report.codeOfConduct",
            },
            {
              component: "br",
              html: "",
              model: null,
            },
            {
              component: "br",
              html: "",
              model: null,
            },
            {
              "component": "p",
              html: "Toimintaohjeistus sisältää seuraavat kohdat",
              hidden: !this.report.codeOfConduct,
              model: null,
            },
            {
              component: "label",
              html: "Lapsityövoima",
              model: null,
            },
            {
              id: "report.codeOfConductBreakdown.childLabor",
              component: "input",
              type: "checkbox",
              style: { marginLeft: '0.5rem' },
              html: null,
              model: "report.codeOfConductBreakdown.childLabor",
            },
            {
              component: "br",
              html: "",
              model: null,
            },
            {
              component: "label",
              html: "Pakkotyövoima",
              model: null,
            },
            {
              id: "report.codeOfConductBreakdown.forcedLabor",
              component: "input",
              type: "checkbox",
              style: { marginLeft: '0.5rem' },
              html: null,
              model: "report.codeOfConductBreakdown.forcedLabor",
            },
            {
              component: "br",
              html: "",
              model: null,
            },
            {
              component: "label",
              html: "Ihmiskauppa",
              model: null,
            },
            {
              id: "report.codeOfConductBreakdown.humanTrafficking",
              component: "input",
              type: "checkbox",
              style: { marginLeft: '0.5rem' },
              html: null,
              model: "report.codeOfConductBreakdown.humanTrafficking",
            },
            {
              component: "br",
              html: "",
              model: null,
            },
            {
              component: "label",
              html: "Syrjintä",
              model: null,
            },
            {
              id: "report.codeOfConductBreakdown.discrimination",
              component: "input",
              type: "checkbox",
              style: { marginLeft: '0.5rem' },
              html: null,
              model: "report.codeOfConductBreakdown.discrimination",
            },
            {
              component: "br",
              html: "",
              model: null,
            },
            {
              component: "label",
              html: "Onnettomuuksien ehkäisy ja työntekijöiden turvallisuus",
              model: null,
            },
            {
              id: "report.codeOfConductBreakdown.accidentPreventionAndWorkerSafety",
              component: "input",
              type: "checkbox",
              style: { marginLeft: '0.5rem' },
              html: null,
              model: "report.codeOfConductBreakdown.accidentPreventionAndWorkerSafety",
            },
            {
              component: "br",
              html: "",
              model: null,
            },
            {
              "component": "br",
              "html": "",
              "model": null,
            },
            {
              component: "button",
              html: "Jatka",
              model: null,
              class: "action-btn documents-ready light",
              click: "test",
            },
          ],
          "waitForAction": true,
          "readDuration": 3000
        },

        {
          "type": "incoming",
          "content": [
            {
              component: "p",
              html: "Onko työntekijöidenne keskuudessa tapahtunut seuraavia ihmisoikeusrikkomuksia?",
              model: null,
            },
          ],
          "waitForAction": false,
          "readDuration": 1500
        },
        {
          "type": "outgoing",
          "contentBuilder": () => [
            {
              component: "label",
              html: "Lapsityövoima",
              model: null,
            },
            {
              id: "report.humanRightsIncidentsDesc.childLabor",
              component: "input",
              type: "checkbox",
              style: { marginLeft: '0.5rem' },
              html: null,
              model: "report.humanRightsIncidentsDesc.childLabor",
            },
            {
              component: "br",
              html: "",
              model: null,
            },
            {
              component: "label",
              html: "Pakkotyövoima",
              model: null,
            },
            {
              id: "report.humanRightsIncidentsDesc.forcedLabor",
              component: "input",
              type: "checkbox",
              style: { marginLeft: '0.5rem' },
              html: null,
              model: "report.humanRightsIncidentsDesc.forcedLabor",
            },
            {
              component: "br",
              html: "",
              model: null,
            },
            {
              component: "label",
              html: "Ihmiskauppa",
              model: null,
            },
            {
              id: "report.humanRightsIncidentsDesc.humanTrafficking",
              component: "input",
              type: "checkbox",
              style: { marginLeft: '0.5rem' },
              html: null,
              model: "report.humanRightsIncidentsDesc.humanTrafficking",
            },
            {
              component: "br",
              html: "",
              model: null,
            },
            {
              component: "label",
              html: "Syrjintä",
              model: null,
            },
            {
              id: "report.humanRightsIncidentsDesc.discrimination",
              component: "input",
              type: "checkbox",
              style: { marginLeft: '0.5rem' },
              html: null,
              model: "report.humanRightsIncidentsDesc.discrimination",
            },
            {
              component: "br",
              html: "",
              model: null,
            },
            {
              "component": "br",
              "html": "",
              "model": null,
            },
            {
              component: "button",
              html: "Jatka",
              model: null,
              class: "action-btn documents-ready light",
              click: "test",
            },
          ],
          "waitForAction": true,
          "readDuration": 3000
        },

        {
          "type": "incoming",
          "content": [
            {
              component: "p",
              html: "Onko arvoketjun työntekijöiden, asiakkaiden tai loppukäyttäjien keskuudesta tullut tietoisuuteenne edellä mainittuja ihmisoikeusrikkomuksia?",
              model: null,
            },
          ],
          "waitForAction": false,
          "readDuration": 1500
        },
        {
          "type": "outgoing",
          "contentBuilder": () => [
            {
              component: "label",
              html: "Ihmisoikeusrikkomuksia havaittu?",
              model: null,
            },
            {
              component: "br",
              html: "",
              model: null,
            },
            {
              id: "valueChainHumanRightsIncidents",
              component: "input",
              type: "checkbox",
              html: null,
              model: "report.valueChainHumanRightsIncidents",
            },
            {
              component: "br",
              html: "",
              model: null,
            },
            {
              "component": "br",
              "html": "",
              "model": null,
            },
            {
              component: "button",
              html: "Jatka",
              model: null,
              class: "action-btn documents-ready light",
              click: "test",
            },
          ],
          "waitForAction": true,
          "readDuration": 3000
        },

        {
          "type": "incoming",
          "content": [
            {
              component: "p",
              html: "Kiitos, raportti on nyt valmis. Pyytäisimme vielä antamaan sähköpostiosoitteen mihin voimme lähettää raportin.",
              model: null,
            },
          ],
          "waitForAction": false,
          "readDuration": 1500
        },
        {
          "type": "outgoing",
          "contentBuilder": () => [
            {
              component: "input",
              class: "input",
              html: null,
              model: "report.email",
            },
            {
              component: "br",
              html: "",
              model: null,
            },
            {
              "component": "br",
              "html": "",
              "model": null,
            },
            {
              component: "button",
              html: "Lähetä",
              model: null,
              disabled: this.report.email === "",
              class: "action-btn documents-ready light",
              click: "finishReport",
            },
          ],
          "waitForAction": true,
          "readDuration": 3000
        },
      ])
    },
    methods: {
      openFileExplorer() {
        document.getElementById('upload-file-input').click()
      },
      getMessageContent(message) {
        return typeof message.contentBuilder === 'function'
          ? message.contentBuilder()
          : (message.content || []);
      },
      async handleEvent(fnName, e) {
        await this[fnName]()
      },
      getValue(path) {
        return path.split('.').reduce((acc, part) => acc?.[part], this);
      },
      setValue(path, value) {
        const parts = path.replace(/\[(\w+)\]/g, '.$1').split('.');
        const last = parts.pop();
        const obj = parts.reduce((acc, part) => acc?.[part], this);
        if (obj && last !== undefined) obj[last] = value;
      },
      capitalize(str) {
        return str ? `${str.slice(0, 1).toUpperCase()}${str.slice(1).toLowerCase()}` : ""
      },
      createIncomingChat(content) {
        const chatLine = document.createElement("p")
        chatLine.classList.add("chat", "incoming")
        chatLine.innerHTML = content

        return chatLine
      },
      createOutgoingChat(content) {
        const chatLine = document.createElement("p")
        chatLine.classList.add("chat", "outgoing")
        chatLine.innerHTML = content

        return chatLine
      },
      async wait(ms) {
        return new Promise((resolve) => {
          setTimeout(resolve, ms);
        })
      },
      async beginStartSequence() {
        const chatbox = document.querySelector(".chatbox")
        const itemsAfterCurrentStep = this.messages.filter((item, index) => index >= this.step)
        const whereToWaitForAction = this.messages.indexOf(itemsAfterCurrentStep.find(item => item.waitForAction))
        const nextSteps = this.messages.filter((item, index) => index >= this.step && index <= whereToWaitForAction)
        
        for (const msg of nextSteps) {
          if (!msg.waitForAction) {
            const child = chatbox.appendChild(msg.type === "incoming" ? this.createIncomingChat('...') : this.createOutgoingChat('...'))
            await this.wait(1000)
            chatbox.removeChild(child)
          }
          this.step = this.step + 1
          setTimeout(() => {
            chatbox.scrollTo({ top: chatbox.scrollHeight, behavior: 'smooth' })
          }, 10)

          await this.wait(msg.readDuration)
        }
      },
      async handleChatToggle() {
        document.querySelector("#main").classList.toggle("show-chatbot")
        
        if (this.step === 0) {
          await this.beginStartSequence()
        }
      },
      async test() {
        await this.beginStartSequence()
      },
      addProperty() {
        this.report = {
          ...this.report,
          b124evii: [
            ...this.report.b124evii,
            {
              geometry: {
                address: "",
                coordinates: [],
              },
              buildingsArea: 0,
              sealedArea: 0,
              b534c: 0,
              b534d: 0,
            },
          ]
        }
      },
      async getCompanyData() {
        this.loading = true

        const response = await fetch("https://fetchcompanyfinancials-rigz32vn7q-uc.a.run.app", {
          method: "POST",
          body: JSON.stringify({
            businessId: this.report.b124ei,
          }),
        })
        const result = await response.json()

        const companyDataResponse = await fetch(`https://avoindata.prh.fi/opendata-ytj-api/v3/companies?businessId=${this.report.b124ei}`)
        const companyDataResult = await companyDataResponse.json()

        this.report = {
          ...this.report,
          ...result,
          companyName: companyDataResult.companies[0].names[0].name,
          headquarters: `${companyDataResult.companies[0].addresses[0].street} ${companyDataResult.companies[0].addresses[0].buildingNumber}, ${companyDataResult.companies[0].addresses[0].postCode} Finland`,
          // nace koodit
          b123eii: Object.entries(naceCodes).filter(item => item[0] === String(companyDataResult.companies[0].mainBusinessLine.type)).map(item => item[1]),
        }

        this.loading = false
        await this.beginStartSequence()
      },
      async addProperties() {
        this.loading = true

        try {
          const addresses = this.report.b124evii ? this.report.b124evii.map(item => item.geometry.address) : []

          const response = await fetch("https://fetchbuildingdata-rigz32vn7q-uc.a.run.app", {
            method: "POST",
            headers: {
              "Content-Type": "application/pdf",
            },
            body: JSON.stringify({
              addresses: [...new Set([...addresses])],
            }),
          })
          const result = await response.json()

          this.report = {
            ...this.report,
            b124evii: result.map(item => ({
              geometry: {
                ...item.geometry,
                coordinates: item.geometry.coordinates.map(i => ({
                  lat: i[0],
                  lon: i[1],
                })),
              },
              buildingsArea: item.buildings.reduce((acc, curr) => acc + Number(curr[0].properties.gross_floor_area), 0),
              sealedArea: item.buildings.reduce((acc, curr) => acc + Number(curr[0].properties.gross_floor_area), 0),
              b533: item.buildings.reduce((acc, curr) => {
                const val = curr.b533 ? Number(curr[0].properties.total_area) : 0
                return acc + val
              }, 0),
              heatingEnergySources: item.buildings.map(item => ({
                buildingSize: item[0].properties.gross_floor_area,
                heatingSource: item[0].properties.heating_energy_source ? item[0].properties.heating_energy_source.slice(-2) : null,
              }))
            }))
          }

          this.loading = false
          await this.beginStartSequence()
        } catch {
          this.loading = false
        }
      },
      async saveProperties() {
        this.report = {
          ...this.report,
          electricityUsage: this.report.b124evii.map(item => ({
            address: item.geometry.address,
            total: 0,
            renewable: 0,
            nonRenewable: 0,
          })),
          heating: this.report.b124evii.map(item => ({
            address: item.geometry.address,
            total: 0,
            renewable: 0,
            nonRenewable: 0,
          })),
          waterUsage: this.report.b124evii.map(item => ({
            address: item.geometry.address,
            total: 0,
          })),
          wasteCollection: this.report.b124evii.map(item => ({
            address: item.geometry.address,
            types: {
              mixedWaste: 0,
              bioWaste: 0,
              plastic: 0,
              cardboard: 0,
              paper: 0,
              glass: 0,
              metal: 0,
            },
          }))
        }

        this.report = {
          ...this.report,
          electricityUsage: this.report.electricityUsage.map(item => ({
            ...item,
            nonRenewable: item.nonRenewable > 0 ? item.nonRenewable : 47,
            realTotal: item.total > 0,
            renewable: item.renewable > 0 ? item.renewable : 53,
            total: item.total > 0
              ? item.total
              : this.report.b124evii.find(i => i.geometry.address === item.address).sealedArea * 150,
          })),
          heating: this.report.heating.map(item => ({
            ...item,
            realTotal: item.total > 0,
            total: item.total > 0
              ? item.total
              : this.report.b124evii.find(i => i.geometry.address === item.address).heatingEnergySources.filter(i => i.heatingSource).reduce((acc, curr) => acc + curr.buildingSize * this.heatingEnergySourcesList[curr.heatingSource].avgConsumption, 0),
            renewable: item.renewable > 0
              ? item.renewable
              : this.report.b124evii.find(i => i.geometry.address === item.address).heatingEnergySources.filter(i => i.heatingSource).reduce((acc, curr) => acc + this.heatingEnergySourcesList[curr.heatingSource].avgRenewable, 0),
            nonRenewable: item.nonRenewable > 0
              ? item.nonRenewable
              : this.report.b124evii.find(i => i.geometry.address === item.address).heatingEnergySources.filter(i => i.heatingSource).reduce((acc, curr) => acc + this.heatingEnergySourcesList[curr.heatingSource].avgNonRenewable, 0) / this.report.b124evii.find(i => i.geometry.address === item.address).heatingEnergySources.filter(i => i.heatingSource).length,
          })),
          waterUsage: this.report.waterUsage.map(item => ({
            ...item,
            realTotal: item.total > 0,
            total: item.total > 0
              ? item.total
              : (this.report.b124evii.find(i => i.geometry.address === item.address).sealedArea * 400) / 1000,
          })),
          wasteCollection: this.report.wasteCollection.map(item => ({
            ...item,
            realTotal: Object.values(item.types).some(val => Number(val) > 0),
          })),
        }

        await this.beginStartSequence()
      },
      revenuesFromCertainSectors() {
        return (this.report.coalTransporatationRevenue ? Number(this.report.coalTransporatationRevenue) : 0) >= (this.report.b124eiv * 0.01) ||
        (this.report.fuelTransportationRevenue ? Number(this.report.fuelTransportationRevenue) : 0) >= (this.report.b124eiv * 0.1) ||
        (this.report.gasTransportationRevenue ? Number(this.report.gasTransportationRevenue) : 0) >= (this.report.b124eiv * 0.5)
      },
      async finishReport() {
        this.loading = true

        try {
          await addDoc(collection(db, 'reports'), {
            ...this.report,
          })

          var element = document.getElementById('report');
          element.style.width = "595pt"
          element.style.display = "block"

          await html2pdf(element);
          
          element.style.display = "none"

          this.loading = false
        } catch {
          element.style.display = "none"
          this.loading = false
        }
      }
    },
  }
</script>

<style>
  @font-face {
    font-family: "Poppins";
    src: url('https://fonts.googleapis.com/css2?family=Poppins:ital,wght@0,100;0,200;0,300;0,400;0,500;0,600;0,700;0,800;0,900;1,100;1,200;1,300;1,400;1,500;1,600;1,700;1,800;1,900&display=swap');
  }
  * {
    margin: 0;
    padding: 0;
    box-sizing: border-box;
    font-family: "Poppins";
  }
  .chatbot {
    position: fixed;
    right: 15vw;
    bottom: 15vh;
    width: 70vw;
    height: 70vh;
    background-color: #FFF;
    border-radius: 15px;
    box-shadow: 0 0 128px 0 rgba(0, 0, 0, 0.33), 0 32px 64px -48px rgba(0, 0, 0, 0.5);
    transform: scale(0.5);
    opacity: 0;
    pointer-events: none;
  }
  .show-chatbot .chatbot {
    transform: scale(1);
    opacity: 1;
    pointer-events: auto;
  }
  .chatbot header {
    background-color: #26b760;
    padding: 16px 0;
    text-align: center;
    border-top-left-radius: 15px;
    border-top-right-radius: 15px;
  }
  .chatbot header h2 {
    color: #FFF;
    font-size: 1.4rem;
  }
  .chatbot-toggler {
    position: fixed;
    right: 40px;
    bottom: 35px;
    height: 50px;
    width: 50px;
    color: #FFF;
    border: none;
    outline: none;
    cursor: pointer;
    background-color: #26b760;
    border-radius: 50%;
    border: 1px solid #FFF;
    display: flex;
    align-items: center;
    justify-content: center;
  } 
  .chatbot-toggler span {
    position: absolute;
  }
  .show-chatbot .chatbot-toggler span:first-child,
  .chatbot-toggler span:last-child {
    opacity: 0;
  }
  .show-chatbot .chatbot-toggler span:last-child {
    opacity: 1;
  }
  .action-btn {
    border-radius: 15px;
    padding: 0.5rem;
    background-color: #26b760;
    color: #FFF;
    border: none;
    outline: none;
    cursor: pointer;
  }
  .light {
    background-color: #F2F2F2;
    color: #000
  }
  .input {
    margin-block: 0.5rem;
    border-radius: 15px;
    border: none;
    padding: 0.5rem;
    width: 20vw;
  }
  .chatbot .chatbox {
    height: 64vh;
    overflow-y: auto;
    padding: 15px 20px 70px;
    display: flex;
    flex-direction: column;
  }
  .incoming {
    margin: 5px 0;
    color: #000;
    background-color: #F2F2F2;
    border-radius: 10px 10px 10px 0;
    width: fit-content;
    padding: 10px;
    max-width: 75%;
  }
  .incoming p {
    padding: 0; margin: 0;
  }
  .outgoing {
    margin: 5px 0;
    color: #FFF;
    background-color: #26b760;
    align-self: flex-end;
    border-radius: 10px 10px 0 10px;
    width: fit-content;
    padding: 10px;
    max-width: 75%;
    justify-content: flex-end;
  }
  .incoming, .outgoing h5 {
    margin-block: 5px;
  }
  table {
    border-collapse: collapse;
  }
  table, td, th {
    border: 1px solid black;
    padding: 0.5rem;
  }
  .round {
    width: 2rem;
    height: 2rem;
    border-radius: 50%;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
  }
  .loader {
    border: 4px solid #f3f3f3; /* Light grey */
    border-top: 4px solid #26b760; /* Blue */
    border-radius: 50%;
    width: 1rem;
    height: 1rem;
    animation: spin 2s linear infinite;
    margin-left: 0.5rem;
  }
  .report p {
    margin: 0;
    padding: 0;
  }

  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
</style>

<style scoped>
  h5 {
    color: whitesmoke
  }
</style>